import VisibilityInformation from '@ben/orion/schema-form/values/components/visibility-information';
import { memo, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { TypographyVariant } from '../../../utils/theme';
import Icon, { IconName } from '../../icon/icon';
import { IconScale } from '../../icon/types';
import InlineTextButton from '../../inline-text-button/inline-text-button';
import Link from '../../link/link';
import { getHref } from '../../schema-form/utils';
import { Tooltip } from '../../tooltip/tooltip';
import {
  ActionsContainer,
  DocumentContainer,
  DocumentDescription,
  TitleAndDescriptionContainer,
  TitleWrapper,
  TRUNCATED_LENGTH,
} from '../file-upload-lists.styles';
import type { EditableFile } from './file-editing-container/file-editing-container';
import FileEditingContainer from './file-editing-container/file-editing-container';

interface Copy {
  edit: string;
  delete: string;
  noDescription: string;
}

interface DetailedFileUploadListProps {
  disabled?: boolean;
  files: EditableFile[];
  onDelete: (index: number) => void;
  onSave: (index: number, fileChanges: Partial<EditableFile>) => void;
  copy?: Copy;
}

export function DetailedFileUploadList({
  disabled = false,
  files,
  onDelete,
  onSave,
  copy = {
    edit: 'Edit',
    delete: 'Delete',
    noDescription: '(No description)',
  },
}: Readonly<DetailedFileUploadListProps>) {
  const theme = useTheme();
  const [filesBeingEdited, setFilesBeingEdited] = useState<number[]>([]);

  const onToggleEdit = (index: number) => {
    setFilesBeingEdited((previousState) => {
      return previousState.includes(index)
        ? previousState.filter((item) => item !== index)
        : [...previousState, index];
    });
  };

  return (
    <StyledDocumentList data-testid="detailed-file-upload-list">
      {files.map((file, index) => {
        const { uuid, description, name, isPolicyDocument } = file;
        const isFileBeingEdited = filesBeingEdited.includes(index);
        const isNameTruncated = name.length > TRUNCATED_LENGTH;
        return (
          <ListItem key={uuid} data-testid="detailed-file-item">
            <DocumentHeader>
              <DocumentContainer>
                <Icon name={IconName.Document} scale={IconScale.S} />
              </DocumentContainer>
              <TitleAndDescriptionContainer>
                <TitleWrapper>
                  <Tooltip tooltipText={isNameTruncated ? name : undefined}>
                    <Link
                      href={getHref(file.file)}
                      color={theme.palette.primary.main}
                      label={
                        isNameTruncated
                          ? name.slice(0, TRUNCATED_LENGTH) + '…'
                          : name
                      }
                      isDownload
                    />
                  </Tooltip>
                </TitleWrapper>
                {!isFileBeingEdited && (
                  <>
                    <DocumentDescription
                      variant={TypographyVariant.bodyMd1}
                      color={theme.palette.neutral[description ? 100 : 80]}
                    >
                      {description || copy.noDescription}
                    </DocumentDescription>
                    <VisibilityInformation
                      isPolicyDocument={isPolicyDocument}
                    />
                  </>
                )}
              </TitleAndDescriptionContainer>
              <ActionsContainer>
                <InlineTextButton
                  aria-label={`${copy.edit} ${name}`}
                  iconName={IconName.Edit}
                  onClick={() => onToggleEdit(index)}
                  label={copy.edit}
                  disabled={isFileBeingEdited || disabled}
                  isLeftIcon
                />
                <InlineTextButton
                  aria-label={`${copy.delete} ${name}`}
                  iconName={IconName.Trash}
                  onClick={() => onDelete(index)}
                  label={copy.delete}
                  disabled={isFileBeingEdited || disabled}
                  isLeftIcon
                />
              </ActionsContainer>
            </DocumentHeader>
            {isFileBeingEdited && (
              <FileEditingContainer
                file={file}
                index={index}
                onCancel={(index) => onToggleEdit(index)}
                onSave={onSave}
              />
            )}
          </ListItem>
        );
      })}
    </StyledDocumentList>
  );
}

export default memo(DetailedFileUploadList);

const StyledDocumentList = styled.ul`
  ${({ theme }) => css`
    padding-left: 0;
    margin: 0;
    list-style-type: none;
    display: flex;
    flex-direction: column;
    width: 100%;
    border-bottom: 1px solid ${theme.palette.neutral[50]};
    padding-top: ${theme.spacing(2)};
    margin-bottom: ${theme.spacing(2)};
  `}
`;

const ListItem = styled.li`
  ${({ theme }) => css`
    border-top: 1px solid ${theme.palette.neutral[50]};
    padding-top: ${theme.spacing(2)};
    display: flex;
    flex-direction: column;
  `}
`;

const DocumentHeader = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: flex-start;
  align-items: flex-start;
  padding-bottom: 16px;
`;
